import { toast } from "sonner";

import { isApiRequestError } from "./apiRequestError";
import { formatApiErrorForToast, formatUserFacingApiCopy } from "./apiErrorDisplay";
import { resolveErrorPresentation } from "./errorPresentation";
import { humanizeApiUserCopy } from "./humanizeApiUserCopy";

export type ApiToastStatus = "success" | "error" | "warning" | "info";

export function sanitizeToastCopy(text: string): string {
  return humanizeApiUserCopy(
    text
      .replace(/\s*—\s*/g, ". ")
      .replace(/\s*–\s*/g, ". ")
      .replace(/\.\s+\./g, ".")
      .trim()
  );
}

export function showApiErrorToast(options: {
  title: string;
  description?: string;
  status?: ApiToastStatus;
  duration?: number;
  slug?: string;
}): void {
  const { title, description, status = "error", duration, slug: _slug } = options;
  const sanitizedTitle = sanitizeToastCopy(title);
  const sanitizedDescription = description ? sanitizeToastCopy(description) : undefined;
  const opts: { description?: string; duration?: number } = {};
  if (sanitizedDescription) opts.description = sanitizedDescription;
  if (duration != null) opts.duration = duration;
  const hasOpts = Object.keys(opts).length > 0;

  switch (status) {
    case "success":
      toast.success(sanitizedTitle, hasOpts ? opts : undefined);
      break;
    case "warning":
      toast.warning(sanitizedTitle, hasOpts ? opts : undefined);
      break;
    case "info":
      toast.info(sanitizedTitle, hasOpts ? opts : undefined);
      break;
    default:
      toast.error(sanitizedTitle, hasOpts ? opts : undefined);
  }
}

/** Present a parsed API error as a Sonner toast when placement is `toast` or `retry`. */
export function showParsedApiErrorToast(options: {
  err: unknown;
  fallbackTitle: string;
  isMutation?: boolean;
  isPageLoad?: boolean;
}): void {
  const { err, fallbackTitle, isMutation = true, isPageLoad = false } = options;

  if (isApiRequestError(err)) {
    const placement = resolveErrorPresentation(err.slug, { isMutation, isPageLoad });
    if (placement === "none" || placement === "page_banner" || placement === "inline") {
      return;
    }
    if (placement === "blocking_modal") {
      return;
    }
    const { title, description } = formatApiErrorForToast(err, fallbackTitle);
    showApiErrorToast({
      title,
      description,
      status: placement === "retry" ? "warning" : "error",
      slug: err.slug,
    });
    return;
  }

  const { title, description } = formatApiErrorForToast(err, fallbackTitle);
  showApiErrorToast({
    title,
    description,
  });
}

/** Use in catch blocks for non-fatal API failures (still Sonner warning toasts). */
export function presentMutationWarning(err: unknown, fallbackTitle: string): void {
  if (isApiRequestError(err)) {
    const placement = resolveErrorPresentation(err.slug, { isMutation: true });
    if (placement === "none" || placement === "page_banner" || placement === "inline") {
      return;
    }
    const { title, description } = formatApiErrorForToast(err, fallbackTitle);
    toastWarning(title, { description });
    return;
  }
  toastWarning(fallbackTitle, {
    description: err instanceof Error ? err.message : undefined,
  });
}

/** Use in catch blocks for API/mutation failures. */
export function presentMutationError(err: unknown, fallbackTitle: string): void {
  showParsedApiErrorToast({ err, fallbackTitle, isMutation: true });
}

/** Present envelope copy when the API returns message/action without throwing. */
export function toastUserFacingError(
  fallbackTitle: string,
  copy: { message?: string; action?: string }
): void {
  const { title, description } = formatUserFacingApiCopy({
    message: copy.message,
    action: copy.action,
    fallbackTitle,
  });
  toastError(title, { description });
}

/** Sonner error toast with shared copy sanitization. */
export function toastError(
  title: string,
  options?: { description?: string; duration?: number }
): void {
  showApiErrorToast({
    title,
    description: options?.description,
    status: "error",
    duration: options?.duration,
  });
}

/** Sonner warning toast with shared copy sanitization. */
export function toastWarning(
  title: string,
  options?: { description?: string; duration?: number }
): void {
  showApiErrorToast({
    title,
    description: options?.description,
    status: "warning",
    duration: options?.duration,
  });
}

/** @deprecated Use showApiErrorToast — re-export for broker call sites during migration */
export function brokerSonnerToast(options: {
  title: string;
  description?: string;
  status?: ApiToastStatus;
  duration?: number;
}): void {
  showApiErrorToast(options);
}
